/**
 * Compact centered confirm shell — same layout/CSS as invitee {@link RentConfirmationModal}
 * (white card, title row + X, body, blue Ok). Use for in-flow errors that should not use toast.
 */

import { useId } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock'
import modalStyles from '@/components/ui/modals.module.css'

export type ConfirmShellModalProps = {
  isOpen: boolean
  title: string
  /** HTML from `formatApiError()` — same contract as legacy `showError` / `showApiError`. */
  bodyHtml: string
  onClose: () => void
  okLabel?: string
  overlayDataTestId?: string
  panelDataTestId?: string
  closeButtonDataTestId?: string
  okButtonDataTestId?: string
  titleId?: string
}

export function ConfirmShellModal({
  isOpen,
  title,
  bodyHtml,
  onClose,
  okLabel = 'Ok',
  overlayDataTestId,
  panelDataTestId,
  closeButtonDataTestId,
  okButtonDataTestId,
  titleId: titleIdProp,
}: ConfirmShellModalProps) {
  const genId = useId()
  const titleId = titleIdProp ?? genId
  useBodyScrollLock(isOpen)

  if (!isOpen) return null

  const content = (
    <div
      data-testid={overlayDataTestId}
      className={`${modalStyles.modalOverlay} !z-[10001]`}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div
        data-testid={panelDataTestId}
        className={`${modalStyles.modalContent} ${modalStyles.modalContentCompact}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={modalStyles.modalHeaderConfirmTight}>
          <h3 id={titleId}>{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className={modalStyles.closeButton}
            aria-label="Close"
            data-testid={closeButtonDataTestId}
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className={modalStyles.modalBodyConfirmTight}>
          <div
            className="max-h-[min(50vh,280px)] overflow-y-auto text-left text-sm leading-relaxed text-[#232946] [&_li]:my-1 [&_li]:text-[#232946] [&_ul]:my-0 [&_ul]:list-disc [&_ul]:pl-5"
            dangerouslySetInnerHTML={{ __html: bodyHtml }}
          />
        </div>
        <div className={modalStyles.modalFooterConfirmTight}>
          <button
            type="button"
            onClick={onClose}
            className={modalStyles.btnPrimary}
            data-testid={okButtonDataTestId}
          >
            {okLabel}
          </button>
        </div>
      </div>
    </div>
  )

  return createPortal(content, document.body)
}
